/**
 * ¿ESTÁ BIEN EL Nº DE REGISTRO DE ESTE PRODUCTO? (F4 del plan del cuaderno,
 * 26-09-2026; D2: H1 + H2.)
 *
 * `resolveRegistration` contesta «qué nº se IMPRIME» en un PDF y cae al del
 * inventario si el de la línea no vale. Esto contesta otra pregunta, la de las
 * pantallas donde se da de alta, se mira o se usa un producto: «¿qué le pasa al
 * nº que tiene?», para AVISAR. Nunca bloquea (T4): el aviso se enseña y el
 * producto se guarda, se planifica y se aplica igual.
 *
 * Los avisos, uno como mucho y en este orden de gravedad:
 *   H2 · `missing`         — sin nº (solo donde el nº tiene sentido: químicos, o
 *                            si no se sabe la categoría);
 *   H2 · `junk`            — basura: «Es», «5L», el lote puesto como nº…;
 *   H2 · `not_in_registry` — bien escrito pero no existe en el MAPA («554» en
 *                            COSTAR: los nacionales empiezan en el 11179);
 *   H2 · `other_product`   — el nº existe y es de OTRO producto (AGRIMEC con el
 *                            22649, que es SPIDERMEC);
 *   H1 · `not_current`     — es el suyo, pero el MAPA lo tiene cancelado (MOVENTO
 *                            GOLD, ES-00024): el bidón que ya está en la nave
 *                            lleva ese nº y se sigue imprimiendo; se avisa para
 *                            que nadie lo compre creyendo que sigue autorizado.
 *
 * UNA SOLA REGLA con la del PDF: el juicio lo hace `resolveRegistration` con el
 * nº como único candidato, así que lo que aquí sale como «de otro producto» es
 * exactamente lo que el PDF no imprime, y una errata del nombre con el nº bueno
 * (KILEO / KYLEO) no avisa en ninguno de los dos.
 *
 * SIN CATÁLOGO NO HAY «OK» (A15): si no ha llegado el catálogo del MAPA y el nº
 * está bien escrito, el estado es `no_data` —no se sabe—, nunca `ok`. Quien
 * pinta no enseña un «correcto» que nadie ha comprobado.
 *
 * APAGADO (D26): con el Registro MAPA apagado para la empresa (Chinampa, RSCO de
 * México) no hay avisos de nº: `off`.
 *
 * PURO: solo importa el resolvedor, que solo importa `normalizeRegistration`.
 */
import {
  resolveRegistration,
  type RegistryIndex,
  type RegistrationWarning,
} from "@/lib/registry/resolveRegistration";

export type RegistrationIssue = "missing" | "junk" | "not_in_registry" | "other_product" | "not_current";

/**
 *  - `ok`: comprobado contra el catálogo y es el suyo, vigente.
 *  - `issue`: hay un aviso (`issue`).
 *  - `no_data`: bien escrito pero sin catálogo con que comprobarlo (A15).
 *  - `not_applicable`: sin nº y no hace falta (abono, biológico).
 *  - `off`: la empresa tiene apagado el Registro MAPA (D26).
 */
export type RegistrationCheckState = "ok" | "issue" | "no_data" | "not_applicable" | "off";

export interface RegistrationCheck {
  state: RegistrationCheckState;
  issue: RegistrationIssue | null;
  /** El nº tal cual lo tiene el producto (recortado), o null. */
  raw: string | null;
  /** El nº como lo escribe el MAPA, cuando se encontró. */
  num: string | null;
  /** El nombre de la ficha del MAPA: el del OTRO producto, o el suyo. */
  registryName: string | null;
}

export interface ValidateRegistrationInput {
  /** `inventory.registration_number` (o lo que se está escribiendo en el alta). */
  registrationNumber: string | null | undefined;
  productName: string | null | undefined;
  /**
   * `inventory.category`. Decide solo si «sin nº» es un aviso: lo es en los
   * químicos (pesticide, fungicide, herbicide) y cuando no se sabe la categoría;
   * un abono o un biológico sin nº no avisa. Un nº puesto se valida siempre.
   */
  category?: string | null;
  /** Lote del producto o de la línea: un nº igual al lote es el lote mal puesto. */
  batch?: string | null;
}

export interface ValidateRegistrationOptions {
  /** El catálogo del MAPA (`buildRegistryIndex`). Sin él: `no_data`, nunca `ok`. */
  catalog?: RegistryIndex | null;
  /** D26: `false` para las empresas con el Registro MAPA apagado. Por defecto, true. */
  mapaValidation?: boolean;
}

/** Categorías en las que el nº de registro tiene sentido (las de `inventoryRequiredFields`). */
const CHEMICAL = new Set(["pesticide", "fungicide", "herbicide"]);

function registrationApplies(category: string | null | undefined): boolean {
  const c = (category ?? "").trim().toLowerCase();
  return !c || CHEMICAL.has(c);
}

const JUNK: ReadonlySet<RegistrationWarning["kind"]> = new Set(["bad_format", "equals_batch", "looks_like_quantity"]);

function result(
  state: RegistrationCheckState,
  issue: RegistrationIssue | null,
  raw: string | null,
  extra: Partial<Pick<RegistrationCheck, "num" | "registryName">> = {},
): RegistrationCheck {
  return { state, issue, raw, num: extra.num ?? null, registryName: extra.registryName ?? null };
}

export function validateRegistration(
  input: ValidateRegistrationInput,
  options: ValidateRegistrationOptions = {},
): RegistrationCheck {
  const raw = typeof input.registrationNumber === "string" ? input.registrationNumber.trim() : "";
  if (options.mapaValidation === false) return result("off", null, raw || null);

  if (!raw) {
    return registrationApplies(input.category)
      ? result("issue", "missing", null)
      : result("not_applicable", null, null);
  }

  const catalog = options.catalog ?? null;
  const r = resolveRegistration(
    { snapshot: raw, productName: input.productName, batch: input.batch },
    { catalog, mapaValidation: true },
  );
  const first = r.warnings[0];

  if (r.value == null) {
    if (first && JUNK.has(first.kind)) return result("issue", "junk", raw);
    if (first?.kind === "not_in_registry") return result("issue", "not_in_registry", raw);
    if (first?.kind === "other_product") {
      return result("issue", "other_product", raw, { registryName: first.registryName });
    }
    // No debería llegar aquí (un solo candidato no vacío): ante la duda, basura.
    return result("issue", "junk", raw);
  }

  if (r.warnings.some((w) => w.kind === "not_current")) {
    return result("issue", "not_current", raw, { num: r.value, registryName: r.registryName });
  }
  // Bien escrito y sin catálogo: no se sabe si existe (A15).
  if (!catalog) return result("no_data", null, raw);
  return result("ok", null, raw, { num: r.value, registryName: r.registryName });
}

/** ¿Hay algo que enseñar? Solo los avisos: `ok`, `no_data`, `off` y `not_applicable` no pintan nada. */
export function registrationIssue(check: RegistrationCheck | null | undefined): RegistrationIssue | null {
  return check?.state === "issue" ? check.issue : null;
}
